import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Toaster, toast } from "sonner";
import {
    ArrowUpRight,
    Linkedin,
    Mail,
    Sparkles,
    PenLine,
    Camera,
    Scissors,
    Send,
    Compass,
    Check,
    Quote,
    Play,
    MapPin,
    Clapperboard,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { EXPERTISES } from "../data/expertises";
import { CITIES } from "../data/cities";
import { EXPERTISE_PAGES_ENABLED } from "../lib/flags";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LINKEDIN_URL = "https://www.linkedin.com/in/fabienmoutier/";
const EMAIL = "fabien@brandyourexpertise.com";
const ICLOSED_BASE = "https://app.iclosed.io/e/FabienMoutier/pr-sentation";
// Track a Plausible custom event (no-op if Plausible isn't loaded yet)
const trackEvent = (name, props) => {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
        window.plausible(name, props ? { props } : undefined);
    }
};
// Build iClosed booking URL with UTM params to trace which CTA converted
const bookingUrl = (source) => {
    const utm = new URLSearchParams({
        utm_source: "brandyourexpertise",
        utm_medium: "landing",
        utm_campaign: "24-shorts-2-vsl",
        utm_content: source,
    }).toString();
    return `${ICLOSED_BASE}?${utm}`;
};
// Reusable click handler for booking CTAs → fires a Plausible goal
const onBookingClick = (source) => () =>
    trackEvent("Book Call", { source });

const PORTFOLIO_VIDEOS = [
    {
        title: "Ellie Attia — Banque Privée",
        tag: "CEO · Finance",
        credit: "Agence Personnelle",
        src: "https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/qllhtsbj_Ellie%20Attia%20Banque%20privee%20video%20short%20ceo.mp4",
        heroSrc: "/videos/hero/ellie-attia.mp4",
    },
    {
        title: "Édouard Bauman",
        tag: "Personal Branding",
        credit: "Agence Personnelle",
        src: "https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/t9ty6961_Edouard%20Bauman%20Video%20ecrite%20par%20Fabien%20Moutier%20Personnal%20Branding%20.mp4",
        heroSrc: "/videos/hero/edouard.mp4",
    },
    {
        title: "Magali Vicente",
        tag: "Communication politique",
        credit: "Agence Personnelle",
        src: "https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/r94qacze_%20magalievicente%20communication%20politique%20menton.mp4",
        heroSrc: "/videos/hero/magali.mp4",
    },
    {
        title: "Ellie Atia",
        tag: "Dirigeants · Short",
        credit: "Agence Personnelle",
        src: "https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/cuscky13_Ellie%20Atia%202%20Video%20Short%20Dirigeants.mp4",
        heroSrc: "/videos/hero/ellie-atia-2.mp4",
    },
    {
        title: "Lola — IA & Personal Branding",
        tag: "Founder · Tech",
        credit: "Agence Personnelle",
        src: "https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/yfaybbhg_IA%20Short%20Video%20Personal%20branding%20Lola.mp4",
        heroSrc: "/videos/hero/lola.mp4",
    },
    {
        title: "Morgan Real Estate",
        tag: "Immobilier · Belgique",
        credit: "Studio Next Door",
        src: "/videos/morgan-real-estate.mp4",
        heroSrc: "/videos/hero/morgan.mp4",
    },
    {
        title: "Daniele Talata — Zidane",
        tag: "Personal Branding · Sport",
        credit: "Brand Your Expertise",
        src: "/videos/daniele-zidane.mp4",
        heroSrc: "/videos/hero/daniele.mp4",
    },
];

const VSL_VIDEO_SRC = "/videos/vsl-demo.mp4";
const VSL_HERO_SRC = "/videos/hero/vsl.mp4";

const HERO_VIDEOS = [
    ...PORTFOLIO_VIDEOS,
    {
        title: "VSL — Méthodologie",
        tag: "VSL longue",
        src: VSL_VIDEO_SRC,
        heroSrc: VSL_HERO_SRC,
    },
];

const PROCESS_STEPS = [
    {
        n: "01",
        icon: Compass,
        title: "Stratégie",
        desc: "Audit de votre positionnement, ligne éditoriale signature, angles d'autorité. On part de votre vision, pas d'une recette toute faite.",
    },
    {
        n: "02",
        icon: PenLine,
        title: "Scripts",
        desc: "J'écris chaque script à votre voix. Hooks calibrés, narration claire, intention claire. Vous lisez. Vous validez.",
    },
    {
        n: "03",
        icon: Camera,
        title: "Tournage",
        desc: "Je suis présent sur le tournage. Direction, cadre, prompteur, énergie. Studio loué à Lausanne ou Genève selon votre préférence. Une demi-journée, vous restez concentré sur votre message.",
    },
    {
        n: "04",
        icon: Scissors,
        title: "Montage",
        desc: "Montage premium, sous-titres dynamiques, sound design. Chaque vidéo prête à scroll-stopper sur LinkedIn.",
    },
    {
        n: "05",
        icon: Send,
        title: "Posting",
        desc: "Calendrier, copywriting du post, publication, engagement initial. On poste pour vous. Vous récoltez les leads.",
    },
];

const TESTIMONIALS = [
    {
        quote: "Ça rassure, ça marche. En 2 mois, j'ai eu beaucoup de clients.",
        name: "Ellie Attia",
        role: "CEO, Banque Privée",
    },
    {
        quote: "... Les sujets étaient tous très pertinents, parfaitement adaptés à mon activité et avec un gros potentiel de viralité pour créer une audience large et qualifiée.",
        name: "Édouard Bauman",
        role: "Dirigeant",
    },
    {
        quote: "Fabien sait faire émerger une voix personnelle et incarnée.",
        name: "Julie Borgeaud",
        role: "Créatrice de contenus · Ex-rédactrice en chef Marie Claire Maison",
    },
];

const FAQ = [
    {
        q: "Que couvre exactement l'offre ?",
        a: "Trois niveaux au choix. Script (900 CHF HT) : stratégie éditoriale, 24 sujets & scripts, 3 hooks par vidéo, indications de tournage. Production (3'000 CHF HT) : tout le pack Script + montage pro, dérush, sous-titres, motion design, supervision monteurs (option 2 VSL scriptées +320 CHF). Studio (5'500 CHF HT) : tout le pack Production + 2 VSL scriptées incluses + tournage en studio pro à Lausanne ou Genève, coaching prise de parole, setup image/lumière/son.",
    },
    {
        q: "Sur quelle durée se déroule l'accompagnement ?",
        a: "Le rythme est calibré avec vous. La plupart de mes clients étalent les 24 vidéos sur 3 à 6 mois pour rester premium dans le feed et capitaliser sur les retours.",
    },
    {
        q: "Qui écrit les scripts ?",
        a: "Moi. Chaque script. À votre voix, à partir de nos sessions stratégie. Vous gardez un droit d'édition total avant tournage.",
    },
    {
        q: "Combien de jours de tournage ?",
        a: "Une à deux demi-journées suffisent pour 24 vidéos courtes + les 2 VSL. Le tournage a lieu uniquement en studio (Lausanne ou Genève).",
    },
    {
        q: "Et si je n'ai pas de matériel ?",
        a: "L'équipement de tournage et la post-production sont inclus. Vous arrivez. Vous parlez. Le reste est pris en charge.",
    },
    {
        q: "Vous travaillez avec quels profils ?",
        a: "Dirigeants, fondateurs, top management, professions libérales et personnalités publiques. Pas de freelance débutant : l'offre est calibrée C-level.",
    },
    {
        q: "Paiement en plusieurs fois ?",
        a: "Le paiement peut être étalé jusqu'à 5 fois, soit 1'000 CHF / mois. Les livraisons des vidéos se font au fur et à mesure des paiements.",
    },
    {
        q: "Paiement en euros ?",
        a: "Oui, c'est possible. Le tarif sera adapté selon le cours CHF / EUR du moment.",
    },
    {
        q: "Délai de livraison ?",
        a: "À partir de 30 jours.",
    },
];

const useReveal = () => {
    useEffect(() => {
        const els = document.querySelectorAll(".bye-fadeup");
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        obs.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 },
        );
        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);
};

const Nav = () => {
    return (
        <header
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/60 border-b border-white/10"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    className="flex items-center gap-2"
                    data-testid="brand-logo"
                >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#c39d63]" />
                    <span className="font-serif text-lg md:text-xl tracking-tight text-white">
                        Brand Your{" "}
                        <span className="bye-gold-text italic">Expertise</span>
                    </span>
                </a>
                <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
                    <a
                        href="#process"
                        className="hover:text-white transition-colors"
                        data-testid="nav-process"
                    >
                        Méthode
                    </a>
                    <a
                        href="#offer"
                        className="hover:text-white transition-colors"
                        data-testid="nav-offer"
                    >
                        Offre
                    </a>
                    <a
                        href="#portfolio"
                        className="hover:text-white transition-colors"
                        data-testid="nav-portfolio"
                    >
                        Portfolio
                    </a>
                    <a
                        href="#faq"
                        className="hover:text-white transition-colors"
                        data-testid="nav-faq"
                    >
                        FAQ
                    </a>
                </nav>
                <a
                    href={bookingUrl("nav")}
                    onClick={onBookingClick("nav")}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                    data-testid="nav-cta-contact"
                >
                    Planifier un rendez-vous
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
            </div>
        </header>
    );
};

const LazyVideo = ({ src, className, ariaLabel }) => {
    const ref = useRef(null);
    const [shouldLoad, setShouldLoad] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setShouldLoad(true);
                        obs.unobserve(e.target);
                    }
                });
            },
            { rootMargin: "200px", threshold: 0.01 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    useEffect(() => {
        const el = ref.current;
        if (!el || !shouldLoad) return;
        el.load();
        const playPromise = el.play();
        if (playPromise && playPromise.catch) {
            playPromise.catch(() => {});
        }
    }, [shouldLoad]);
    return (
        <video
            ref={ref}
            className={className}
            muted
            loop
            playsInline
            preload="none"
            aria-label={ariaLabel}
        >
            {shouldLoad && <source src={src} type="video/mp4" />}
        </video>
    );
};

const heroPosterFromSrc = (heroSrc) =>
    heroSrc
        ? heroSrc.replace("/videos/hero/", "/images/hero-posters/").replace(".mp4", ".jpg")
        : undefined;

const HeroCollageTile = ({ src, poster }) => (
    <video
        className="absolute inset-0 w-full h-full object-cover bye-hero-video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        poster={poster}
    >
        <source src={src} type="video/mp4" />
    </video>
);

const HeroVideoCollage = () => {
    const gridRef = useRef(null);
    // Mobile fallback: on first user interaction, play any paused hero videos
    // (protects against iOS Low Power Mode blocking autoplay)
    useEffect(() => {
        const playAll = () => {
            const container = gridRef.current;
            if (!container) return;
            container
                .querySelectorAll("video")
                .forEach((v) => v.play().catch(() => {}));
        };
        const onInteract = () => {
            playAll();
            window.removeEventListener("touchstart", onInteract);
            window.removeEventListener("click", onInteract);
            window.removeEventListener("scroll", onInteract);
        };
        window.addEventListener("touchstart", onInteract, {
            once: true,
            passive: true,
        });
        window.addEventListener("click", onInteract, { once: true });
        window.addEventListener("scroll", onInteract, {
            once: true,
            passive: true,
        });
        return () => {
            window.removeEventListener("touchstart", onInteract);
            window.removeEventListener("click", onInteract);
            window.removeEventListener("scroll", onInteract);
        };
    }, []);

    // 5 columns of vertical videos with offsets + drift animation, heavy dark scrim on top
    const v = HERO_VIDEOS;
    // Mobile: 2 columns × 3 tiles (only 6 unique tiles, ~600KB total)
    // Desktop: 5 columns × 3 tiles (all videos, ~1.3MB total)
    const isMobile =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(max-width: 768px)").matches;

    const desktopCols = [
        { videos: [v[0], v[2], v[4]], top: "-4%", duration: "46s" },
        { videos: [v[1], v[3], v[5]], top: "6%", duration: "54s", reverse: true },
        { videos: [v[6 % v.length], v[2], v[1]], top: "-8%", duration: "50s" },
        { videos: [v[3], v[0], v[5]], top: "4%", duration: "58s", reverse: true },
        { videos: [v[4], v[6 % v.length], v[3]], top: "-2%", duration: "48s" },
    ];
    const mobileCols = [
        { videos: [v[0], v[2], v[6 % v.length]], top: "-4%", duration: "52s" },
        { videos: [v[1], v[5], v[3]], top: "4%", duration: "58s", reverse: true },
    ];
    const cols = isMobile ? mobileCols : desktopCols;

    return (
        <div
            ref={gridRef}
            className="absolute inset-0 overflow-hidden"
            aria-hidden="true"
            data-testid="hero-video-collage"
        >
            <div className="bye-hero-grid">
                {cols.map((c) => (
                    <div
                        key={`${c.top}-${c.duration}`}
                        className={`bye-hero-col ${c.reverse ? "is-reverse" : ""}`}
                        style={{
                            top: c.top,
                            animationDuration: c.duration,
                        }}
                    >
                        {[...c.videos, ...c.videos].map((vid, j) => {
                            const heroSrc = vid.heroSrc || vid.src;
                            return (
                                <div
                                    key={`${c.top}-${j}-${heroSrc}`}
                                    className="bye-hero-tile"
                                >
                                    <HeroCollageTile
                                        src={heroSrc}
                                        poster={heroPosterFromSrc(vid.heroSrc)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

const ROTATING_PLATFORMS = [
    { name: "LinkedIn", color: "#4F86E8" },
    { name: "Instagram", color: "#F47AAE" },
    { name: "TikTok", color: "#7AE7DC" },
    { name: "YouTube", color: "#FF6B6B" },
];

const SLOT_INTERVAL = 2200;
const SLOT_DURATION = 700;

const RotatingPlatform = () => {
    const [pos, setPos] = useState(0);
    const [animating, setAnimating] = useState(true);
    const [widths, setWidths] = useState([]);
    const measureRefs = useRef([]);
    const len = ROTATING_PLATFORMS.length;

    // Measure each word's natural italic width to drive smooth width transitions
    useEffect(() => {
        const measure = () => {
            const w = measureRefs.current.map(
                (el) => el?.getBoundingClientRect().width || 0,
            );
            setWidths(w);
        };
        measure();
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(measure);
        }
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setAnimating(true);
            setPos((p) => p + 1);
        }, SLOT_INTERVAL);
        return () => clearInterval(id);
    }, []);

    // Seamless loop: snap back from duplicated item to index 0
    useEffect(() => {
        if (pos === len) {
            const snap = setTimeout(() => {
                setAnimating(false);
                setPos(0);
                requestAnimationFrame(() =>
                    requestAnimationFrame(() => setAnimating(true)),
                );
            }, SLOT_DURATION + 30);
            return () => clearTimeout(snap);
        }
    }, [pos, len]);

    const items = [...ROTATING_PLATFORMS, ROTATING_PLATFORMS[0]];
    const currentIndex = pos % len;
    const currentWidth = widths[currentIndex];
    const easing = "cubic-bezier(0.85, 0, 0.15, 1)";

    return (
        <span
            className="bye-slot"
            data-testid="hero-rotating-platform"
            aria-live="polite"
            style={{
                width: currentWidth ? `${currentWidth}px` : "auto",
                transition: animating
                    ? `width ${SLOT_DURATION}ms ${easing}`
                    : "none",
            }}
        >
            {/* In-flow invisible sizer — drives baseline + initial natural width */}
            <span className="bye-slot__vsizer italic">
                {ROTATING_PLATFORMS[currentIndex].name}
            </span>
            {/* Hidden measurers (off-screen) — used to know each word's width */}
            <span aria-hidden="true" className="bye-slot__measurer">
                {ROTATING_PLATFORMS.map((p, i) => (
                    <span
                        key={p.name}
                        ref={(el) => (measureRefs.current[i] = el)}
                        className="bye-slot__measurer-item italic"
                    >
                        {p.name}
                    </span>
                ))}
            </span>
            {/* Viewport overlays the sizer exactly — same dimensions, clips the slot animation */}
            <span className="bye-slot__viewport">
                <span
                    className="bye-slot__track"
                    style={{
                        top: `-${pos * 100}%`,
                        transition: animating
                            ? `top ${SLOT_DURATION}ms ${easing}`
                            : "none",
                    }}
                >
                    {items.map((p, i) => (
                        <span
                            key={`${p.name}-${i}`}
                            className="bye-slot__item italic"
                            style={{ color: p.color }}
                        >
                            {p.name}
                        </span>
                    ))}
                </span>
            </span>
        </span>
    );
};

const Hero = () => {
    return (
        <section
            id="top"
            className="relative min-h-[100svh] flex items-end overflow-hidden"
            data-testid="hero-section"
        >
            <HeroVideoCollage />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/92 via-[#050505]/55 to-[#050505]/15" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/35 to-transparent" />
            <div className="absolute inset-0 bye-hero-vignette opacity-70" />
            <div className="absolute inset-0 bye-grain" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pb-28 w-full">
                <div className="max-w-4xl">
                    <div className="bye-fadeup flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-8">
                        <span className="w-8 h-px bg-[#c39d63]" />
                        Brand Your Expertise · par Fabien Moutier
                    </div>
                    <h1
                        className="bye-fadeup font-serif text-lg sm:text-xl lg:text-2xl leading-[1.25] tracking-tight text-white/80 max-w-2xl"
                        style={{ transitionDelay: "60ms" }}
                    >
                        Agence de{" "}
                        <span className="bye-gold-text">Personal Branding</span>{" "}
                        et Vidéo LinkedIn pour dirigeants à{" "}
                        <span className="italic">Genève</span> et{" "}
                        <span className="italic">Lausanne</span>
                    </h1>
                    <h2
                        className="bye-fadeup mt-6 md:mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[1] tracking-tight text-white bye-hero-headline"
                        style={{ transitionDelay: "180ms" }}
                    >
                        <span className="block">
                            Devenez la{" "}
                            <span className="italic bye-headline-accent">
                                référence
                            </span>
                        </span>
                        <span className="block">de votre secteur</span>
                        <span className="block mt-2">
                            grâce aux{" "}
                            <span className="italic bye-gold-underline">
                                vidéos courtes.
                            </span>
                        </span>
                    </h2>
                    <p
                        className="bye-fadeup mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed"
                        style={{ transitionDelay: "300ms" }}
                    >
                        J'écris, je tourne et je publie pour les dirigeants. De
                        la stratégie au montage, je vous accompagne de A à Z.
                        Vous restez focus sur votre business — votre marque
                        personnelle prend la lumière.
                    </p>
                    <p
                        className="bye-fadeup mt-4 max-w-2xl text-[11px] md:text-xs italic text-white/40 leading-relaxed"
                        style={{ transitionDelay: "360ms" }}
                        data-testid="hero-credits"
                    >
                        Crédits vidéos : Écriture Fabien Moutier / Brand Your
                        Expertise — Réalisation Agence Personnelle, Studio
                        Next Door et Brand Your Expertise.
                    </p>
                    <div
                        className="bye-fadeup mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
                        style={{ transitionDelay: "360ms" }}
                    >
                        <a
                            href={bookingUrl("hero")}
                            onClick={onBookingClick("hero")}
                            target="_blank"
                            rel="noreferrer"
                            className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                            data-testid="hero-cta-primary"
                        >
                            Planifier un rendez-vous
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href="#portfolio"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/20 text-white text-sm hover:border-white/50 transition-colors"
                            data-testid="hero-cta-portfolio"
                        >
                            <Play className="w-4 h-4 text-[#c39d63]" />
                            Voir le portfolio
                        </a>
                    </div>
                </div>
            </div>
            <div className="absolute bottom-6 right-6 md:right-12 hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/40">
                <span className="w-6 h-px bg-white/30" />
                Scroll
            </div>
        </section>
    );
};

const useCountUp = (target, { duration = 1600, start = false } = {}) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!start) return;
        let rafId;
        const t0 = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const tick = (now) => {
            const elapsed = Math.min((now - t0) / duration, 1);
            const v = Math.round(target * ease(elapsed));
            setValue(v);
            if (elapsed < 1) rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [target, duration, start]);
    return value;
};

const formatNumber = (n) => n.toLocaleString("fr-FR").replace(/\s/g, "\u202F");

const CountUpStat = ({ prefix = "", target, suffix = "", duration = 1600 }) => {
    const ref = useRef(null);
    const [start, setStart] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setStart(true);
                        obs.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.4 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    const value = useCountUp(target, { duration, start });
    return (
        <span
            ref={ref}
            className="tabular-nums"
            data-testid={`countup-${target}`}
        >
            {prefix}
            {formatNumber(value)}
            {suffix}
        </span>
    );
};

const Stats = () => {
    const items = [
        {
            node: <CountUpStat prefix="+" target={60} duration={1600} />,
            l: "Dirigeants accompagnés",
        },
        {
            node: <CountUpStat prefix="+" target={1000} duration={1800} />,
            l: "Scripts rédigés",
        },
        { v: "A → Z", l: "Stratégie, tournage, posting" },
        {
            node: <CountUpStat target={100} suffix="%" duration={1500} />,
            l: "Écrit à votre voix",
        },
    ];
    return (
        <section
            className="relative border-y border-white/10 bg-[#080808]"
            data-testid="stats-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
                {items.map((it, i) => (
                    <div
                        key={it.l}
                        className="py-10 md:py-14 px-4 md:px-8 bye-fadeup"
                        style={{ transitionDelay: `${i * 80}ms` }}
                    >
                        <div className="font-serif text-4xl md:text-5xl tracking-tight text-white">
                            {it.node || it.v}
                        </div>
                        <div className="mt-2 text-xs md:text-sm tracking-wide text-white/55">
                            {it.l}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};


const STUDIOS = [
    {
        city: "Lausanne",
        region: "Vaud · Suisse romande",
        desc: "Studio chaleureux signature, ambiance feutrée et intime. Idéal pour les interviews longues, les VSL et les contenus narratifs.",
        img: "/images/studios/lausanne.jpg",
        tags: ["Reels", "Instagram", "LinkedIn", "VSL", "Lumière chaude"],
    },
    {
        city: "Genève",
        region: "GE · Suisse romande",
        desc: "Studio premium au coeur de Genève, équipement broadcast et lumière maîtrisée. Pensé pour les dirigeants pressés et les formats shorts.",
        img: "/images/studios/geneve.avif",
        tags: ["Shorts LinkedIn", "Lumière studio", "Setup 4K"],
    },
];

const Studios = () => {
    return (
        <section
            id="studios"
            className="relative py-24 md:py-32 overflow-hidden bg-[#070707]"
            data-testid="studios-section"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 20% 0%, rgba(195,157,99,0.08), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(195,157,99,0.05), transparent 55%)",
                }}
            />
            <div className="absolute inset-0 bye-grain pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-end mb-14 md:mb-20">
                    <div className="lg:col-span-7 bye-fadeup">
                        <div className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                            <span>Nos studios — Suisse romande</span>
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.02]">
                            Choisissez votre studio :
                            <br />
                            <span className="italic text-white/70">
                                Lausanne ou Genève.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-white/65 text-base md:text-lg leading-relaxed bye-fadeup">
                        Deux espaces de tournage premium pensés pour les
                        dirigeants romands. On vient à vous, ou vous venez chez
                        nous — vous choisissez le décor qui colle à votre image.
                    </p>
                </div>

                {/* 2 studios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                    {STUDIOS.map((s, i) => (
                        <div
                            key={s.city}
                            className="bye-card rounded-2xl overflow-hidden bye-fadeup group"
                            style={{ transitionDelay: `${i * 100}ms` }}
                            data-testid={`studio-card-${s.city.toLowerCase()}`}
                        >
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                    src={s.img}
                                    alt={`Studio ${s.city} — tournage Personal Branding`}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm border border-white/10 text-[10px] tracking-[0.2em] uppercase text-white/80">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#c39d63]" />
                                    {s.region}
                                </div>
                                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                                    <h3 className="font-serif text-3xl md:text-4xl text-white leading-none">
                                        {s.city}
                                    </h3>
                                    <MapPin
                                        className="w-5 h-5 text-[#c39d63] shrink-0"
                                        strokeWidth={1.5}
                                    />
                                </div>
                            </div>
                            <div className="p-6 md:p-8">
                                <p className="text-sm md:text-base text-white/65 leading-relaxed mb-5">
                                    {s.desc}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {s.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-[10px] tracking-[0.18em] uppercase text-white/55 px-2.5 py-1 rounded-full border border-white/10"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const CtaBand = ({ id, eyebrow, headline, italic, sub, testid }) => {
    return (
        <section
            id={id}
            className="relative py-20 md:py-28 border-y border-white/10 overflow-hidden"
            data-testid={testid}
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 50%, rgba(195,157,99,0.10), transparent 60%)",
                }}
            />
            <div className="absolute inset-0 bye-grain pointer-events-none" />
            <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center bye-fadeup">
                <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                    {eyebrow}
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
                    {headline}{" "}
                    <span className="italic text-white/70">{italic}</span>
                </h3>
                {sub && (
                    <p className="mt-5 text-white/60 max-w-xl mx-auto text-base">
                        {sub}
                    </p>
                )}
                <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                        href={bookingUrl(testid || "cta-band")}
                        onClick={onBookingClick(testid || "cta-band")}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                        data-testid={`${testid}-booking`}
                    >
                        Planifier un rendez-vous
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <a
                        href={`mailto:${EMAIL}`}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white text-sm hover:border-white/40 transition-colors"
                        data-testid={`${testid}-email`}
                    >
                        <Mail className="w-4 h-4 text-[#c39d63]" />
                        Écrire un message
                    </a>
                </div>
            </div>
        </section>
    );
};

const Process = () => {
    return (
        <section
            id="process"
            className="relative py-24 md:py-32"
            data-testid="process-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end mb-14 md:mb-20">
                    <div className="lg:col-span-5 bye-fadeup">
                        <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                            La méthode
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
                            De la stratégie
                            <br />
                            <span className="italic text-white/70">
                                à la conversion.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-6 lg:col-start-7 text-white/65 text-base md:text-lg leading-relaxed bye-fadeup">
                        Vous parlez. Le reste, je m'en occupe. Six étapes
                        cousues main pour transformer votre expertise en
                        contenus qui font autorité — et votre visibilité en
                        leads qualifiés. Sans jamais sacrifier votre agenda.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
                    {PROCESS_STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.n}
                                className="bye-card p-8 md:p-10 bye-fadeup"
                                style={{ transitionDelay: `${i * 80}ms` }}
                                data-testid={`process-step-${s.n}`}
                            >
                                <div className="flex items-center justify-between mb-8">
                                    <span className="font-serif text-3xl bye-gold-text">
                                        {s.n}
                                    </span>
                                    <Icon
                                        className="w-5 h-5 text-[#c39d63]"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-sm md:text-base text-white/60 leading-relaxed">
                                    {s.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

const OFFER_TIERS = [
    {
        id: "script",
        eyebrow: "01 — Script",
        title: "Script",
        subtitle: "La stratégie & les mots",
        price: "900",
        priceUnit: "CHF HT",
        implication: "Vous tournez et montez",
        cta: "Choisir Script",
        utm: "offer-script",
        highlighted: false,
        features: [
            "Stratégie éditoriale signature",
            "24 sujets & scripts sur-mesure",
            "3 hooks par vidéo",
            "Script complet + angle & intention",
            "Indications précises de tournage",
        ],
    },
    {
        id: "production",
        eyebrow: "02 — Production",
        title: "Production",
        subtitle: "Du script à la vidéo finalisée",
        price: "3 000",
        priceUnit: "CHF HT",
        implication: "Vous tournez, je gère la post-production",
        cta: "Choisir Production",
        utm: "offer-production",
        highlighted: true,
        badge: "Le plus choisi",
        features: [
            "Tout le pack Script",
            "Montage professionnel",
            "Dérush & édition",
            "Sous-titres, stickers & covers",
            "Motion design",
            "Supervision monteurs & contrôle qualité",
        ],
        addon: "Option 2 VSL entièrement scriptées : +320 CHF",
    },
    {
        id: "studio",
        eyebrow: "03 — Studio",
        title: "Studio",
        subtitle: "L'expérience clé en main",
        price: "5 500",
        priceUnit: "CHF HT",
        implication: "Je prends en charge toute la production",
        cta: "Choisir Studio",
        utm: "offer-studio",
        highlighted: false,
        result: "Vous repartez avec 24 vidéos prêtes à diffuser — jusqu'à 6 mois de contenu.",
        features: [
            "Tout le pack Production",
            "2 VSL entièrement scriptées incluses",
            "Tournage en studio professionnel (Lausanne ou Genève)",
            "Direction du tournage & coaching prise de parole",
            "Accompagnement face caméra",
            "Setup image, lumière & son pro",
            "Captation professionnelle",
        ],
    },
];

const OFFER_COMPARISON = {
    "Stratégie & script": {
        rows: [
            ["Stratégie éditoriale", true, true, true],
            ["24 sujets & scripts", true, true, true],
            ["3 hooks par vidéo", true, true, true],
            ["Script complet + angle & intention", true, true, true],
            ["Indications précises de tournage", true, true, true],
        ],
    },
    "VSL": {
        rows: [["2 VSL entièrement scriptées", false, "Option +320 CHF", true]],
    },
    "Post-production": {
        rows: [
            ["Montage professionnel", false, true, true],
            ["Dérush & édition", false, true, true],
            ["Sous-titres, stickers & covers", false, true, true],
            ["Motion design", false, true, true],
            ["Supervision des monteurs & QC", false, true, true],
        ],
    },
    "Tournage & accompagnement": {
        rows: [
            ["Tournage en studio professionnel", false, false, true],
            ["Direction du tournage", false, false, true],
            ["Coaching prise de parole", false, false, true],
            ["Accompagnement face caméra", false, false, true],
            ["Setup image, lumière & son", false, false, true],
            ["Captation professionnelle", false, false, true],
        ],
    },
};

const OfferCell = ({ value }) => {
    if (value === true)
        return (
            <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#c39d63]/15 text-[#c39d63]"
                aria-label="Inclus"
            >
                <Check className="w-3 h-3" strokeWidth={2.5} />
            </span>
        );
    if (value === false)
        return <span className="text-white/25 text-lg leading-none">—</span>;
    return (
        <span className="text-[10px] tracking-[0.15em] uppercase text-[#c39d63]">
            {value}
        </span>
    );
};

const Offer = () => {
    return (
        <section
            id="offer"
            className="relative py-24 md:py-32 overflow-hidden"
            data-testid="offer-section"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 0%, rgba(195,157,99,0.10), transparent 55%)",
                }}
            />
            <div className="absolute inset-0 bye-grain pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20 bye-fadeup">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        L'offre
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.02] mb-6">
                        Choisissez votre{" "}
                        <span className="italic text-white/70">
                            niveau d'accompagnement
                        </span>
                    </h2>
                    <p className="text-white/60 max-w-xl mx-auto leading-relaxed">
                        Une même exigence éditoriale. Trois niveaux de prise en
                        charge.
                    </p>
                </div>

                {/* 3 Tier cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-16 md:mb-20">
                    {OFFER_TIERS.map((tier, i) => (
                        <div
                            key={tier.id}
                            className={`relative bye-fadeup rounded-2xl p-7 md:p-8 flex flex-col ${
                                tier.highlighted
                                    ? "bg-gradient-to-b from-[#191207] to-[#0e0906] border border-[#c39d63]/40 shadow-[0_20px_60px_-20px_rgba(195,157,99,0.35)]"
                                    : "bye-card"
                            }`}
                            style={{ transitionDelay: `${i * 100}ms` }}
                            data-testid={`offer-tier-${tier.id}`}
                        >
                            {tier.badge && (
                                <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-[#c39d63] text-[#0a0a0a] text-[10px] tracking-[0.2em] uppercase font-semibold">
                                    {tier.badge}
                                </div>
                            )}
                            <div
                                className={`text-[10px] tracking-[0.28em] uppercase mb-3 ${
                                    tier.highlighted
                                        ? "text-[#c39d63]"
                                        : "text-white/50"
                                }`}
                            >
                                {tier.eyebrow}
                            </div>
                            <h3 className="font-serif text-3xl md:text-4xl text-white mb-2">
                                {tier.title}
                            </h3>
                            <p className="text-white/55 text-sm mb-7">
                                {tier.subtitle}
                            </p>
                            <div className="flex items-baseline gap-2 mb-2">
                                <span className="font-serif text-5xl text-white tracking-tight">
                                    {tier.price}
                                </span>
                                <span className="text-white/60 text-sm">
                                    {tier.priceUnit}
                                </span>
                            </div>
                            <div className="text-[11px] tracking-[0.18em] uppercase text-white/40 mb-6">
                                {tier.implication}
                            </div>
                            {tier.result && (
                                <div
                                    className="flex items-start gap-2.5 mb-6 rounded-xl border border-[#c39d63]/40 bg-[#c39d63]/10 px-4 py-3"
                                    data-testid={`offer-tier-${tier.id}-result`}
                                >
                                    <Clapperboard
                                        className="w-4 h-4 text-[#c39d63] shrink-0 mt-0.5"
                                        strokeWidth={1.75}
                                    />
                                    <span className="text-[13px] leading-relaxed text-[#e6c891] font-medium">
                                        {tier.result}
                                    </span>
                                </div>
                            )}
                            <ul className="space-y-2.5 mb-7 flex-1">
                                {tier.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2.5 text-white/75 text-sm leading-relaxed"
                                    >
                                        <Check
                                            className="w-3.5 h-3.5 text-[#c39d63] shrink-0 mt-1"
                                            strokeWidth={2.5}
                                        />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            {tier.addon && (
                                <div className="text-xs text-[#c39d63] mb-5 italic">
                                    {tier.addon}
                                </div>
                            )}
                            <a
                                href={bookingUrl(tier.utm)}
                                onClick={onBookingClick(tier.utm)}
                                target="_blank"
                                rel="noreferrer"
                                className={`group inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-medium transition-colors ${
                                    tier.highlighted
                                        ? "bg-[#c39d63] text-[#0a0a0a] hover:bg-[#e6c891]"
                                        : "border border-white/20 text-white hover:border-[#c39d63]/60 hover:text-[#c39d63]"
                                }`}
                                data-testid={`offer-tier-${tier.id}-cta`}
                            >
                                {tier.cta}
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    ))}
                </div>

                {/* Comparison table */}
                <div
                    className="bye-card rounded-2xl overflow-hidden bye-fadeup"
                    data-testid="offer-comparison-table"
                >
                    <div className="px-6 md:px-8 py-5 border-b border-white/8">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#c39d63]">
                            Comparatif détaillé
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-white/8 text-white/50">
                                    <th className="text-left px-6 md:px-8 py-4 font-normal text-[10px] tracking-[0.2em] uppercase w-1/2">
                                        Prestations
                                    </th>
                                    {OFFER_TIERS.map((t) => (
                                        <th
                                            key={t.id}
                                            className={`text-center px-4 py-4 font-normal text-[10px] tracking-[0.2em] uppercase ${
                                                t.highlighted
                                                    ? "text-[#c39d63]"
                                                    : ""
                                            }`}
                                        >
                                            {t.title}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(OFFER_COMPARISON).map(
                                    ([group, { rows }]) => (
                                        <React.Fragment key={group}>
                                            <tr className="bg-white/[0.02]">
                                                <td
                                                    colSpan={4}
                                                    className="px-6 md:px-8 py-3 text-[10px] tracking-[0.24em] uppercase text-[#c39d63]/90"
                                                >
                                                    {group}
                                                </td>
                                            </tr>
                                            {rows.map((row) => (
                                                <tr
                                                    key={row[0]}
                                                    className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                                                >
                                                    <td className="px-6 md:px-8 py-3.5 text-white/80">
                                                        {row[0]}
                                                    </td>
                                                    <td className="text-center py-3.5">
                                                        <OfferCell
                                                            value={row[1]}
                                                        />
                                                    </td>
                                                    <td
                                                        className={`text-center py-3.5 ${
                                                            OFFER_TIERS[1]
                                                                .highlighted
                                                                ? "bg-[#c39d63]/[0.04]"
                                                                : ""
                                                        }`}
                                                    >
                                                        <OfferCell
                                                            value={row[2]}
                                                        />
                                                    </td>
                                                    <td className="text-center py-3.5">
                                                        <OfferCell
                                                            value={row[3]}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ),
                                )}
                                {/* Implication row */}
                                <tr className="border-t border-[#c39d63]/20 bg-[#c39d63]/[0.04]">
                                    <td className="px-6 md:px-8 py-4 text-[10px] tracking-[0.2em] uppercase text-[#c39d63]">
                                        Votre niveau d'implication
                                    </td>
                                    {OFFER_TIERS.map((t) => (
                                        <td
                                            key={t.id}
                                            className={`text-center px-3 py-4 text-xs italic ${
                                                t.highlighted
                                                    ? "text-white"
                                                    : "text-white/60"
                                            }`}
                                        >
                                            {t.implication}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Payment note */}
                <div className="mt-8 text-center bye-fadeup">
                    <p className="text-xs text-white/45 italic">
                        Prix hors taxes en CHF. Paiement également possible en
                        EUR (converti au taux CHF/EUR du moment) · Étalement
                        jusqu'à 5×.
                    </p>
                </div>
            </div>
        </section>
    );
};



const VideoCard = ({ video, index }) => {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);

    const handlePlay = async () => {
        const el = ref.current;
        if (!el) return;
        if (playing) {
            try {
                el.pause();
            } catch (_) {
                /* ignore */
            }
            setPlaying(false);
            return;
        }
        try {
            // Ensure source is loaded before attempting playback
            if (el.readyState < 2) {
                try {
                    el.load();
                } catch (_) {
                    /* ignore */
                }
            }
            const p = el.play();
            if (p && typeof p.then === "function") {
                await p;
            }
            setPlaying(true);
        } catch (err) {
            // Most commonly: autoplay blocked or temporary load issue. Don't crash the page.
            if (process.env.NODE_ENV === "development") {
                console.warn("Video play failed:", err?.message || err);
            }
            try {
                el.muted = true;
                await el.play();
                setPlaying(true);
            } catch (err2) {
                if (process.env.NODE_ENV === "development") {
                    console.warn("Video muted-play also failed:", err2?.message || err2);
                }
                toast.error("Lecture impossible pour le moment. Réessayez.");
                setPlaying(false);
            }
        }
    };

    return (
        <div
            className="group relative shrink-0 w-[260px] md:w-[300px] lg:w-[320px] bye-fadeup"
            style={{ transitionDelay: `${index * 80}ms` }}
            data-testid={`portfolio-video-${index}`}
        >
            <div className="relative aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]">
                <video
                    ref={ref}
                    className="absolute inset-0 w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    controls={playing}
                    onEnded={() => setPlaying(false)}
                    onPause={() => setPlaying(false)}
                    onPlay={() => setPlaying(true)}
                    onError={(e) => {
                        if (process.env.NODE_ENV === "development") {
                            console.warn("video error", e?.currentTarget?.error);
                        }
                    }}
                >
                    <source src={video.src} type="video/mp4" />
                </video>
                {!playing && (
                    <button
                        onClick={handlePlay}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors"
                        data-testid={`portfolio-play-${index}`}
                        aria-label={`Lire la vidéo : ${video.title}`}
                    >
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#c39d63] text-[#0a0a0a] group-hover:scale-105 transition-transform">
                            <Play
                                className="w-5 h-5 ml-0.5"
                                fill="currentColor"
                            />
                        </span>
                    </button>
                )}
            </div>
            <div className="mt-4">
                <div className="text-xs tracking-[0.2em] uppercase text-[#c39d63] mb-1">
                    {video.tag}
                </div>
                <div className="font-serif text-lg text-white">
                    {video.title}
                </div>
                {video.credit && (
                    <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-white/40">
                        Réalisation · {video.credit}
                    </div>
                )}
            </div>
        </div>
    );
};

const Portfolio = () => {
    return (
        <section
            id="portfolio"
            className="relative py-24 md:py-32 bg-[#070707] border-y border-white/5 overflow-hidden"
            data-testid="portfolio-section"
        >
            {/* Subtle VSL cover as background ambiance */}
            <img
                src="https://customer-assets.emergentagent.com/job_linkedin-content-hub-3/artifacts/v2x7pa03_Cover%20Me%CC%81thodologie.png"
                alt=""
                aria-hidden="true"
                className="absolute -right-32 top-1/2 -translate-y-1/2 w-[640px] max-w-[55%] opacity-[0.07] blur-sm pointer-events-none select-none"
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at right, rgba(195,157,99,0.06), transparent 55%)",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 mb-12 md:mb-16">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <div className="bye-fadeup">
                        <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                            Portfolio
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-2xl">
                            Cinq voix.{" "}
                            <span className="italic text-white/70">
                                Une signature.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-md text-white/60 text-sm md:text-base leading-relaxed bye-fadeup">
                        Aperçu de vidéos récentes — CEO, banque privée,
                        communication politique, personal branding. Cliquez
                        pour lire.
                        <span className="block mt-3 text-xs text-white/40">
                            Écriture & stratégie : Fabien Moutier — Réalisation
                            en collaboration avec Agence Personnelle, Studio
                            Next Door et Brand Your Expertise.
                        </span>
                    </p>
                </div>
            </div>

            <div className="relative">
                <div
                    className="overflow-x-auto no-scrollbar px-6 md:px-12 pb-6"
                    data-testid="portfolio-scroll"
                >
                    <div className="flex gap-6 md:gap-8 w-max max-w-none">
                        {PORTFOLIO_VIDEOS.map((v, i) => (
                            <VideoCard key={v.src} video={v} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const About = () => {
    return (
        <section
            id="about"
            className="relative py-24 md:py-32"
            data-testid="about-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                <div className="lg:col-span-5 bye-fadeup">
                    <div className="relative">
                        <div
                            className="absolute -inset-4 rounded-2xl border border-[#c39d63]/30"
                            aria-hidden="true"
                        />
                        <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#c39d63]/10 blur-2xl" />
                        <img
                            src="/images/fabien-portrait.jpg"
                            alt="Fabien Moutier — Brand Your Expertise"
                            className="relative w-full rounded-2xl border border-white/10 object-cover aspect-[4/5]"
                            data-testid="about-photo"
                        />
                        <div className="absolute -bottom-5 left-6 right-6 mx-auto flex items-center justify-between bg-[#0a0a0a]/90 backdrop-blur border border-white/10 rounded-full px-5 py-3">
                            <div>
                                <div className="text-white font-medium leading-none">
                                    Fabien Moutier
                                </div>
                                <div className="text-[11px] tracking-[0.2em] uppercase text-[#c39d63] mt-1">
                                    Fondateur
                                </div>
                            </div>
                            <a
                                href={LINKEDIN_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex w-9 h-9 rounded-full bg-[#c39d63] items-center justify-center text-[#0a0a0a] hover:bg-[#e6c891] transition-colors"
                                data-testid="about-linkedin"
                                aria-label="LinkedIn de Fabien Moutier"
                            >
                                <Linkedin className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7 bye-fadeup">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        Qui est derrière
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-8">
                        Une seule personne.{" "}
                        <span className="italic text-white/70">De A à Z.</span>
                    </h2>
                    <div className="space-y-5 text-white/70 leading-relaxed text-base md:text-lg max-w-xl">
                        <p>
                            Je m'appelle Fabien Moutier. Depuis plusieurs
                            années, j'écris des prises de parole pour des
                            dirigeants exigeants : CEO, fondateurs, top
                            management, personnalités publiques. Plus de 60
                            experts accompagnés, plus de 1 000 scripts rédigés.
                        </p>
                        <p>
                            Vous ne payez pas une agence anonyme : c'est moi qui
                            vous écris, vous dirige sur le plateau et publie
                            vos contenus. Une seule voix, un seul interlocuteur,
                            zéro relais.
                        </p>
                        <p className="text-white/60">
                            <span className="text-[#c39d63]">Studios</span> — Je
                            n'ai pas de studio personnel ; je loue un studio à{" "}
                            <span className="text-white">Lausanne</span> ou{" "}
                            <span className="text-white">Genève</span>, selon
                            votre préférence et votre agenda.
                        </p>
                        <p className="text-white/60">
                            <span className="text-[#c39d63]">Crédits</span> —
                            Pour certaines vidéos présentées ici (Ellie Attia,
                            Magali Vicente, Édouard Bauman), la réalisation a
                            été assurée en collaboration ponctuelle avec
                            l'Agence Personnelle. L'écriture et la stratégie
                            restent les miennes.{" "}
                            <strong className="text-white font-semibold">
                                Je sélectionne toujours des prestataires de
                                montage premium que je supervise moi-même, pour
                                vous.
                            </strong>
                        </p>
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <a
                            href={bookingUrl("about")}
                            onClick={onBookingClick("about")}
                            target="_blank"
                            rel="noreferrer"
                            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                            data-testid="about-cta-booking"
                        >
                            Faire connaissance
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white text-sm hover:border-white/40 transition-colors"
                            data-testid="about-cta-linkedin"
                        >
                            <Linkedin className="w-4 h-4" />
                            Suivre sur LinkedIn
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Testimonials = () => {
    return (
        <section
            id="testimonials"
            className="relative py-24 md:py-32"
            data-testid="testimonials-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="bye-fadeup mb-14 md:mb-20 max-w-3xl">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        Ils en parlent
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
                        Des dirigeants qui ont{" "}
                        <span className="italic text-white/70">repris la main</span>{" "}
                        sur leur narratif.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {TESTIMONIALS.map((t, i) => (
                        <figure
                            key={t.name}
                            className="bye-card rounded-2xl p-8 md:p-10 bye-fadeup"
                            style={{ transitionDelay: `${i * 100}ms` }}
                            data-testid={`testimonial-${i}`}
                        >
                            <Quote
                                className="w-6 h-6 text-[#c39d63] mb-6"
                                strokeWidth={1.5}
                            />
                            <blockquote className="font-serif text-xl md:text-2xl leading-snug text-white/90">
                                « {t.quote} »
                            </blockquote>
                            <figcaption className="mt-8 pt-6 border-t border-white/10">
                                <div className="text-white font-medium">
                                    {t.name}
                                </div>
                                <div className="text-sm text-white/55">
                                    {t.role}
                                </div>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
};

const FAQSection = () => {
    return (
        <section
            id="faq"
            className="relative py-24 md:py-32 bg-[#070707] border-t border-white/5"
            data-testid="faq-section"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                <div className="bye-fadeup mb-12 md:mb-16">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        FAQ
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
                        Les questions qu'on{" "}
                        <span className="italic text-white/70">
                            me pose toujours.
                        </span>
                    </h2>
                </div>

                <Accordion
                    type="single"
                    collapsible
                    className="w-full bye-fadeup"
                    data-testid="faq-accordion"
                >
                    {FAQ.map((f, i) => (
                        <AccordionItem
                            key={f.q}
                            value={`item-${i}`}
                            className="border-white/10"
                            data-testid={`faq-item-${i}`}
                        >
                            <AccordionTrigger
                                className="text-left font-serif text-xl md:text-2xl text-white hover:no-underline hover:text-[#e6c891] data-[state=open]:text-[#e6c891] py-6"
                                data-testid={`faq-trigger-${i}`}
                            >
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-white/65 text-base leading-relaxed pb-6 max-w-3xl">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
};

const Contact = () => {
    return (
        <section
            id="contact"
            className="relative py-24 md:py-32"
            data-testid="contact-section"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16 bye-fadeup">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        Parlons-en
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-6">
                        Votre prochaine{" "}
                        <span className="italic text-white/70">
                            prise de parole
                        </span>{" "}
                        commence ici.
                    </h2>
                    <p className="text-white/65 leading-relaxed max-w-xl mx-auto">
                        Choisissez un créneau qui vous convient ci-dessous —
                        20 minutes suffisent pour cadrer vos objectifs et
                        démarrer.
                    </p>
                </div>

                {/* iClosed inline booking widget */}
                <div
                    className="bye-card rounded-2xl overflow-hidden p-2 sm:p-3 md:p-4 bye-fadeup"
                    data-testid="iclosed-widget-container"
                >
                    <div
                        className="iclosed-widget rounded-xl overflow-hidden"
                        data-url="https://app.iclosed.io/e/FabienMoutier/pr-sentation"
                        title="Échange Stratégique — Brand Your Expertise"
                        style={{ width: "100%", height: "700px" }}
                    />
                </div>

                {/* Direct contact links */}
                <div className="mt-12 md:mt-14 bye-fadeup">
                    <div className="text-center text-xs tracking-[0.28em] uppercase text-white/45 mb-6">
                        Ou contactez-moi directement
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                        <a
                            href={`mailto:${EMAIL}`}
                            className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full border border-white/10 hover:border-[#c39d63]/50 text-white/80 hover:text-white text-sm transition-colors"
                            data-testid="contact-email-link"
                        >
                            <Mail className="w-4 h-4 text-[#c39d63]" />
                            {EMAIL}
                        </a>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full border border-white/10 hover:border-[#c39d63]/50 text-white/80 hover:text-white text-sm transition-colors"
                            data-testid="contact-linkedin-link"
                        >
                            <Linkedin className="w-4 h-4 text-[#c39d63]" />
                            linkedin.com/in/fabienmoutier
                        </a>
                        <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full border border-white/5 text-white/55 text-sm">
                            <Sparkles className="w-4 h-4 text-[#c39d63]" />
                            Réponse personnelle · pas de chatbot
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Footer = () => {
    return (
        <footer
            className="relative py-20 md:py-28 border-t border-white/10 bg-[#040404]"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="bye-fadeup">
                    <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-6">
                        Brand Your Expertise
                    </div>
                    <h3 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-white max-w-4xl leading-[0.95]">
                        Faisons de votre expertise{" "}
                        <span className="italic bye-gold-text">
                            le bruit de fond du marché.
                        </span>
                    </h3>
                </div>

                <div className="bye-divider my-16" />

                <div className={`grid grid-cols-1 ${EXPERTISE_PAGES_ENABLED ? "md:grid-cols-4" : "md:grid-cols-3"} gap-10 text-sm`}>
                    <div>
                        <div className="text-white/40 uppercase tracking-[0.2em] text-xs mb-3">
                            Contact
                        </div>
                        <a
                            href={`mailto:${EMAIL}`}
                            className="text-white/80 hover:text-white block"
                            data-testid="footer-email"
                        >
                            {EMAIL}
                        </a>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="text-white/80 hover:text-white block mt-2"
                            data-testid="footer-linkedin"
                        >
                            linkedin.com/in/fabienmoutier
                        </a>
                    </div>
                    <div>
                        <div className="text-white/40 uppercase tracking-[0.2em] text-xs mb-3">
                            Navigation
                        </div>
                        <a
                            href="#process"
                            className="text-white/80 hover:text-white block"
                        >
                            Méthode
                        </a>
                        <a
                            href="#offer"
                            className="text-white/80 hover:text-white block mt-2"
                        >
                            Offre
                        </a>
                        <a
                            href="#portfolio"
                            className="text-white/80 hover:text-white block mt-2"
                        >
                            Portfolio
                        </a>
                        <a
                            href="#faq"
                            className="text-white/80 hover:text-white block mt-2"
                        >
                            FAQ
                        </a>
                        <div className="text-white/40 uppercase tracking-[0.2em] text-xs mt-5 mb-3">
                            Villes
                        </div>
                        {CITIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/${c.slug}`}
                                className="text-white/70 hover:text-white block mt-1.5"
                                data-testid={`footer-city-${c.slug}`}
                            >
                                Personal branding {c.city}
                            </Link>
                        ))}
                    </div>
                    {EXPERTISE_PAGES_ENABLED && (
                        <div>
                            <div className="text-white/40 uppercase tracking-[0.2em] text-xs mb-3">
                                Expertises
                            </div>
                            {EXPERTISES.map((e) => (
                                <Link
                                    key={e.slug}
                                    to={`/expertise/${e.slug}`}
                                    className="text-white/70 hover:text-white block mt-1.5"
                                    data-testid={`footer-expertise-${e.slug}`}
                                >
                                    {e.navLabel}
                                </Link>
                            ))}
                        </div>
                    )}
                    <div>
                        <div className="text-white/40 uppercase tracking-[0.2em] text-xs mb-3">
                            Studio & crédits
                        </div>
                        <p className="text-white/60 leading-relaxed">
                            Studio loué à Lausanne ou Genève selon votre
                            préférence. Écriture & stratégie : Fabien Moutier.
                            Réalisation en collaboration ponctuelle avec
                            l'Agence Personnelle sur certaines vidéos.
                        </p>
                    </div>
                </div>

                <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/40">
                    <div>
                        © {new Date().getFullYear()} Brand Your Expertise — Tous
                        droits réservés.
                    </div>
                    <div>Pensé pour les dirigeants. Écrit à la main.</div>
                </div>
            </div>
        </footer>
    );
};

const TOAST_OPTIONS = {
    style: {
        background: "#111111",
        border: "1px solid rgba(195,157,99,0.3)",
        color: "#fafafa",
    },
};

const LandingPage = () => {
    useReveal();
    useEffect(() => {
        // best-effort warm ping
        axios.get(`${API}/`).catch(() => {});
    }, []);

    return (
        <div className="App" data-testid="landing-page">
            <Helmet>
                <link rel="canonical" href="https://brandyourexpertise.com/" />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />
            </Helmet>
            <Toaster
                theme="dark"
                position="bottom-right"
                richColors
                toastOptions={TOAST_OPTIONS}
            />
            <Nav />
            <main>
                <Hero />
                <Stats />
                <About />
                <Process />
                <Studios />
                <CtaBand
                    eyebrow="Passons à l'action"
                    headline="Prêt à transformer votre méthode en"
                    italic="présence d'autorité ?"
                    sub="20 minutes pour cadrer vos objectifs, votre cible et la suite. Sans engagement."
                    testid="cta-after-process"
                />
                <Offer />
                <Portfolio />
                <CtaBand
                    eyebrow="Et pour vous ?"
                    headline="Imaginez votre voix dans"
                    italic="ce niveau de production."
                    sub="On définit ensemble vos angles signature et on lance la première saison."
                    testid="cta-after-portfolio"
                />
                <Testimonials />
                <FAQSection />
                <Contact />
            </main>
            <Footer />
        </div>
    );
};

export default LandingPage;
